/**
 * Focus trap utility for accessible modal dialogs
 * Implements WCAG 2.1 AA keyboard navigation requirements for modals
 */
import { getFocusableElements } from './focusable';

/**
 * Creates a focus trap within a modal element
 * @param modalElement - The modal container element
 * @returns Cleanup function to remove the trap
 */
export function createFocusTrap(modalElement: HTMLElement): () => void {
  const focusableElements = getFocusableElements(modalElement);
  const firstFocusable = focusableElements[0];

  // Store the element that had focus before the modal opened
  const previouslyFocusedElement = document.activeElement as HTMLElement;

  // Focus the first focusable element in the modal
  if (firstFocusable) {
    firstFocusable.focus();
  }

  /**
   * Handles tab key to trap focus within the modal
   */
  function handleTabKey(e: KeyboardEvent) {
    const focusableElements = getFocusableElements(modalElement);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.key !== 'Tab') return;

    // Shift + Tab: If on first element, move to last
    if (e.shiftKey && document.activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable?.focus();
      return;
    }

    // Tab: If on last element, move to first
    if (!e.shiftKey && document.activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable?.focus();
      return;
    }
  }

  /**
   * Handles escape key to close modal
   */
  function handleEscapeKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      // Trigger close via the close button if it exists
      const closeButton = modalElement.querySelector('[aria-label*="Close"]') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  // Add event listeners
  modalElement.addEventListener('keydown', handleTabKey);
  modalElement.addEventListener('keydown', handleEscapeKey);

  // Return cleanup function
  return () => {
    modalElement.removeEventListener('keydown', handleTabKey);
    modalElement.removeEventListener('keydown', handleEscapeKey);

    // Restore focus to the previously focused element
    if (previouslyFocusedElement && previouslyFocusedElement.focus) {
      previouslyFocusedElement.focus();
    }
  };
}
